import {BaseElement, MsgLabel, OSkinableInit} from "/@back@/commons/basis.js";
import {Button} from "/@back@/commons/widgets/buttons.js";
import {POPUP} from "/@back@/commons/widgets/popups.js";
import {IReg, IUiEnv, REG} from "/@lib@/commons/registry.js";
import {DOM, JSX} from "/@lib@/commons/xml/dom.js";
import {DOMSH} from "/@lib@/commons/xml/domsh.js";
import {IWspEnv, Wsp} from "/@lib@/wsp/wsp.js";
import {EFolderChildState, FolderEntries, ITEM} from "/@lib@/wsp/item.js";

/**
 * Dialogue d'édition d'un nom d'une source utilisée pour :
 * - créer un espace
 * - renommer un espace ou un item
 * - dupliquer un espace ou un item.
 * - NON CODé: créer un item dont on connaitrait déjà le type d'item et l'extension (ihm simplifiée) =>(ajouter init.defaultCode)
 *
 * L'écran contrôle l'absence du nom cible dans l'arbre si on est dans un espace.
 *
 * Le résultat retourné est le nouveau nom (extension incluse) ou null si abandonné.
 */

export interface SrcNewCode extends BaseElement {
	initialize(init: OSrcNewCodeInit): this
}

export interface OSrcNewCodeInit extends OSkinableInit {

	/** Si airItem, null. */
	parentFolder: string;

	/** Pour renommage (extension incluse), null si création. */
	currentCode?: string

	/** Si création, nom par défaut (extension incluse si item). */
	defaultCode?: string

	/** */
	targetSrcUriType?: 'item' | 'space' | 'res'

	inputLabel?: string

	/** Si true, autorise le remplacement d'un contenu existant. */
	allowReplace?: boolean
	doBtnLabel?: string | ((state?: EFolderChildState, allowReplace?: boolean, doAllChecked?: boolean) => string)

	cancelBtnLabel?: string | ((state?: EFolderChildState, allowReplace?: boolean, doAllChecked?: boolean) => string)

	/** Case à cocher supplémentaire pour tout remplacer/ignorer. */
	doAllBtnLabel?: string

	/** Output. */
	doAllSelected?: boolean
}

export class SrcNewCode extends BaseElement {

	/** Contexte */
	reg: IReg<IWspEnv & IUiEnv>;

	get wsp(): Wsp {return this.reg.env.wsp};

	/** extension incluant le '.' */
	suffix: string;

	/** code de l'espace ou name de l'item (ie code sans extension). */
	currentName: string;

	config: OSrcNewCodeInit;

	nameInput: HTMLInputElement;
	doBtn: Button;
	cancelBtn: Button;
	ctrlMsgAreaBox: MsgLabel;
	selAllArea?: HTMLLabelElement;

	/** Controle de l'existance des noms. */
	folderEntries: FolderEntries;

	protected _initialize(init: OSrcNewCodeInit) {
		this.reg = this.findReg(init);
		this.config = init;
		const sr = this.attachShadow(DOMSH.SHADOWDOM_INIT);
		this.reg.installSkin("webzone:panel", sr);
		this.reg.installSkin("standard-dialog", sr);
		this.reg.installSkin("form-control-areas", sr);
		this._initAndInstallSkin(this.localName, init);

		if (init.parentFolder != null) {
			this.folderEntries = new FolderEntries(this.wsp, this);
			this.folderEntries.setFolderUri(init.parentFolder);
		}

		if (init.currentCode) {
			//Fixation du suffixe que si item.
			const idx = init.targetSrcUriType !== 'item' ? -1 : init.currentCode.lastIndexOf('.');
			if (idx > 0) this.suffix = init.currentCode.substring(idx);
			this.currentName = idx > 0 ? init.currentCode.substring(0, idx) : init.currentCode;
		}

		const main = sr.appendChild(<main/>);
		main.appendChild(<label for="name">{init.inputLabel || "Code"}</label>);
		const ctn = main.appendChild(<div class="input"/>);
		const val = this.currentName || init.defaultCode;
		this.nameInput = ctn.appendChild(<input id="name" autocomplete="off" spellcheck="false" class={this.suffix ? 'file' : 'folder'} oninput={this.onAnyChange} value={val}/>) as HTMLInputElement;
		if (this.suffix) ctn.appendChild(<span class="ext">{this.suffix}</span>);
		if (val) {
			const pos = val.indexOf('.'); //On essaye de se placer avant le 1er '.'
			this.nameInput.setSelectionRange(0, pos > 0 ? pos : val.length);
		}
		this.ctrlMsgAreaBox = main.appendChild(<MsgLabel id="msg"/>) as MsgLabel;

		const footer = sr.appendChild(<div id="footer"/>);
		if (init.doAllBtnLabel) {
			this.selAllArea = footer.appendChild(<label class="option"><input type="checkbox" onchange={this.onAnyChange}/>{init.doAllBtnLabel}</label>) as HTMLLabelElement;
		}
		this.doBtn = footer.appendChild(<Button id="do" class="default" ui-context="dialog" onclick={this.onDoBtn}/>) as Button;
		this.cancelBtn = footer.appendChild(<Button id="cancel" ui-context="dialog" onclick={this.onCancelBtn}/>) as Button;

		this.onkeydown = this.onKeyDown;

		this.nameInput.focus();
		this.evalDoable();
	}

	isDoAllChecked(): boolean | undefined {
		return (this.selAllArea?.firstElementChild as HTMLInputElement)?.checked;
	}

	private refreshConfigOnClose() {
		if (this.selAllArea) this.config.doAllSelected = this.isDoAllChecked();
	}

	validAndDo() {
		if (this.evalDoable() !== 'do') return;
		const newVal = this.nameInput.value;
		this.refreshConfigOnClose();
		POPUP.findPopupableParent(this).close(this.suffix ? newVal + this.suffix : newVal);
	}

	evalDoable(): 'no' | 'do' | 'working' {
		const newVal = this.nameInput.value;
		let msg: string = null;
		let st: 'no' | 'do' | 'working' = 'do';
		let doBtnLabel = this.config.doBtnLabel || "Créer";
		let cancelBtnLabel = this.config.cancelBtnLabel || "Annuler";

		let childStateNow;

		if (!newVal) {
			msg = "Saisissez un code";
			st = 'no';
		} else if (newVal === this.currentName) {
			msg = "Saisissez un nouveau code";
			st = 'no';
		} else {
			const code = this.suffix ? newVal + this.suffix : newVal;
			const r = ITEM.isValidCode(this.config.targetSrcUriType, code);
			if (r !== true) {
				msg = r;
				st = 'no';
			}
			if (this.config.parentFolder != null) {
				//on n'est pas en airItem
				if (this.folderEntries.loading) {
					st = 'working';
					this.folderEntries.loading.then(() => {this.evalDoable()});
				} else {
					childStateNow = this.folderEntries.getChildStateNow(code);
					if (childStateNow === "used") {
						msg = "Ce code existe déjà.";
						if (!this.config.allowReplace) st = 'no';
					}
				}
			}
		}
		this.ctrlMsgAreaBox.setCustomMsg(msg, 'info');
		const doAll = this.isDoAllChecked();
		this.cancelBtn.label = typeof cancelBtnLabel === 'string' ? cancelBtnLabel : cancelBtnLabel(childStateNow, this.config.allowReplace, doAll);
		this.doBtn.label = typeof doBtnLabel === 'string' ? doBtnLabel : doBtnLabel(childStateNow, this.config.allowReplace, doAll);
		this.doBtn.disabled = st !== 'do';
		if (this.selAllArea) DOM.setHidden(this.selAllArea, st !== 'do' || newVal !== this.config.defaultCode);
		return st;
	}

	onAnyChange(this: HTMLInputElement, ev: Event) {
		const itemCreator = DOMSH.findHost(this) as SrcNewCode;
		itemCreator.evalDoable();
	}

	onKeyDown(ev: KeyboardEvent) {
		if (ev.key === 'Enter') {
			this.validAndDo();
			ev.preventDefault();
			ev.stopImmediatePropagation();
		}
	}

	onCancelBtn(this: Button) {
		(DOMSH.findHost(this) as SrcNewCode).refreshConfigOnClose();
		POPUP.findPopupableParent(this).close();
	}

	onDoBtn(this: Button) {
		(DOMSH.findHost(this) as SrcNewCode).validAndDo();
	}
}

REG.reg.registerSkin('wsp-src-newcode', 1, /* language=CSS */ `
	:host {
		min-width: 35em;
		min-height: 9em;
	}

	:focus-visible {
		outline: var(--focus-outline);
	}

	main {
		flex: 1;
		display: flex;
		min-height: 0;
		min-width: 0;
		flex-direction: column;
		justify-content: center;
		padding: .5em;
	}

	.option {
		display: flex;
	}

	.input {
		display: flex;
		min-height: 0;
		min-width: 0;
		align-items: center;
		margin-block-end: .1em;
	}

	input {
		flex: 1;
		font-size: inherit;
	}

	#title {
		font-weight: bolder;
	}

	#msg {
		flex: 1;
		justify-content: flex-end;
		margin: 0 .5em;
		min-width: 15em;
	}

	#msg[hidden] {
		display: unset;
		visibility: hidden;
	}

	c-button {
		min-width: 6em;
	}
`);

customElements.define("wsp-src-newcode", SrcNewCode);
